// src/components/auth/AuthSso.tsx
//
// Platform SSO landing route. The platform launchpad ("Open as this school →
// Learning Diversity") redirects the browser here as
//   https://<app-host>/auth/sso?code=<single-use-handoff-code>
//
// We hand the code to the SHARED `platform-sso-exchange` edge function on SCR
// Central, which verifies it with the platform and mints a Supabase session
// scoped to the LAUNCHED tenant. We then adopt that session so the app renders
// as that school (RLS-scoped).
//
// This is PURELY ADDITIVE — the normal Entra SSO login (`/login` →
// `/auth/callback`) is untouched and remains the only path staff use directly.
import { useEffect, useState } from 'react';
import supabase from '../../services/supabase';

// Shared platform exchange endpoint. Deliberately NOT routed through
// `supabase.functions.invoke`: the function lives on the SCR Central project
// and must be reachable regardless of which project this app's client points
// at, so the URL is explicit.
const EXCHANGE_URL =
  'https://dzwaqfcmxxhtivgsjnox.supabase.co/functions/v1/platform-sso-exchange';

// The slug the platform knows this app by. Must match exactly.
const APP_SLUG = 'learning-diversity';

type Phase = 'working' | 'error';

// Errors surfaced to the user are our own short codes, never raw upstream
// text — no internals, tokens or PII leak into the UI.
type ExchangeErrorCode =
  | 'exchange_failed'
  | 'no_session_returned'
  | 'session_not_persisted';

export const AuthSso = () => {
  const [phase, setPhase] = useState<Phase>('working');
  const [detail, setDetail] = useState<ExchangeErrorCode | null>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const code = new URLSearchParams(window.location.search).get('code');
      if (!code) {
        // Nothing to exchange — send them to the normal login.
        window.location.replace('/login');
        return;
      }

      try {
        // The app's own anon key only authenticates against the app's own
        // project. Send it as the gateway credential when (and only when) this
        // app is already pointed at the shared project; otherwise post
        // unauthenticated and let the function's own verification apply.
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };
        const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as
          | string
          | undefined;
        const appUrl = (import.meta.env.VITE_SUPABASE_URL as string) || '';
        if (anonKey && EXCHANGE_URL.startsWith(appUrl.replace(/\/+$/, ''))) {
          headers.apikey = anonKey;
          headers.Authorization = `Bearer ${anonKey}`;
        }

        const response = await fetch(EXCHANGE_URL, {
          method: 'POST',
          headers,
          body: JSON.stringify({ code, app: APP_SLUG }),
        });

        const payload = (await response.json().catch(() => null)) as {
          access_token?: string;
          refresh_token?: string;
          error?: string;
        } | null;

        if (!response.ok) throw new Error('exchange_failed');

        const accessToken = payload?.access_token;
        const refreshToken = payload?.refresh_token;
        if (!accessToken || !refreshToken) {
          throw new Error(payload?.error ? 'exchange_failed' : 'no_session_returned');
        }

        // Clear cached app state only — deliberately NOT the Supabase session.
        //
        // This must NOT call supabase.auth.signOut({ scope: 'local' }) first:
        //   1. This route renders inside <AuthProvider>, so signOut fires
        //      SIGNED_OUT straight into the auth context that is about to hold
        //      the new session, and the context treats SIGNED_OUT as final.
        //   2. supabase-js broadcasts auth events to every tab on this origin,
        //      so it would also sign the user out of any other open tab.
        // The observed symptom was a launch that appeared to succeed and then
        // dropped back to logged-out about two seconds later. setSession below
        // replaces whatever session was there, so the signOut is unnecessary.
        //
        // Any cached identity/UI state keyed to the PREVIOUS tenant must go
        // before the new session is adopted, otherwise the app can render the
        // previous school's identity and a hard refresh will not clear it.
        // Everything except the `sb-*` Supabase auth keys is app cache
        // (currently just `nav-expanded-sections`), so it is safe to drop.
        try {
          Object.keys(localStorage)
            .filter((key) => !key.startsWith('sb-'))
            .forEach((key) => localStorage.removeItem(key));
        } catch {
          /* storage unavailable — not fatal */
        }

        // Adopt the tenant-scoped session. Run unconditionally: the session
        // switch is a global side effect that must complete even if this
        // component is unmounting.
        const { error: setError } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });
        if (setError) throw new Error('exchange_failed');

        // Confirm the session is actually readable before navigating.
        // setSession persists asynchronously, and a hard redirect that wins
        // that race leaves the app booting with no session — which presents to
        // the user as "signed in, then signed out". Poll rather than assume.
        let adopted = false;
        for (let attempt = 0; attempt < 10; attempt++) {
          const { data: check } = await supabase.auth.getSession();
          if (check?.session?.access_token) {
            adopted = true;
            break;
          }
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
        if (!adopted) throw new Error('session_not_persisted');

        // Full reload so AuthProvider re-initialises cleanly under the new
        // session with no stale cached user. /auth/callback already resolves
        // the profile role and forwards to /staff, /parent or /teacher.
        window.location.replace('/auth/callback');
      } catch (err) {
        if (cancelled) return;
        const code =
          err instanceof Error &&
          ['exchange_failed', 'no_session_returned', 'session_not_persisted'].includes(
            err.message
          )
            ? (err.message as ExchangeErrorCode)
            : 'exchange_failed';
        setDetail(code);
        setPhase('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
      <div className="max-w-md w-full text-center space-y-4">
        <h1 className="text-xl font-semibold text-gray-900">Learning Diversity</h1>
        {phase === 'working' ? (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
            <p className="text-gray-600">Signing you in…</p>
            <p className="text-sm text-gray-500">Connecting to the platform…</p>
          </>
        ) : (
          <>
            <p className="text-red-600">
              We couldn&apos;t complete the sign-in from the platform ({detail}).
            </p>
            <a
              href="/login"
              className="inline-block text-sm text-blue-600 underline hover:text-blue-800"
            >
              Return to sign in
            </a>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthSso;
